import {
  Component,
  computed,
  effect,
  HostListener,
  inject,
  untracked,
} from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ProgressBarModule } from 'primeng/progressbar';
import { NotificationService } from 'src/app/core/services/notification.service';
import { BrochureAnalysisService } from 'src/app/trip-calculator/services/brochure-analysis.service';
import {
  BulkUploadStatus,
  BatchUploadService,
} from 'src/app/trip-calculator/services/batch-upload.service';

/**
 * Global, non-blocking widget that tracks the background bulk upload.
 * Mounted once at the app root so it persists across navigation: the user can
 * keep working on the platform while documents upload and the AI analysis runs.
 *
 * It is the "minimized" view of the upload: it stays hidden while the full
 * modal is open (the modal shows the progress + terminal state itself) and only
 * surfaces once the modal is closed. When the upload finishes in this minimized
 * state, the widget shows the outcome toast and clears itself.
 */
@Component({
  selector: 'app-bulk-upload-progress',
  standalone: true,
  imports: [TranslocoModule, ProgressBarModule],
  templateUrl: './bulk-upload-progress.component.html',
})
export class BulkUploadProgressComponent {
  protected readonly batchUploadService = inject(BatchUploadService);
  private brochureAnalysisService = inject(BrochureAnalysisService);
  private notificationService = inject(NotificationService);
  private transloco = inject(TranslocoService);

  /** Visible while an upload is in flight AND the full modal is closed. */
  protected readonly visible = computed(
    () =>
      this.batchUploadService.state().status !== 'idle' &&
      !this.brochureAnalysisService.showBulkUploadModal(),
  );

  /** Determinate percentage during the S3 upload phase. */
  protected readonly percent = computed(() =>
    this.batchUploadService.uploadPercent(),
  );

  constructor() {
    // When the upload reaches a terminal state while minimized (modal closed),
    // surface the outcome toast and clear the widget. If the modal is open, it
    // owns the terminal view and its own close/reset, so we stay out of it.
    effect(() => {
      const { status, total, failed } = this.batchUploadService.state();
      const isTerminal = status === 'done' || status === 'error';
      if (!isTerminal || this.brochureAnalysisService.showBulkUploadModal())
        return;
      untracked(() => this.surfaceOutcome(status, total, failed));
    });
  }

  private surfaceOutcome(
    status: BulkUploadStatus,
    total: number,
    failed: number,
  ): void {
    if (status === 'error') {
      this.notificationService.error(
        this.transloco.translate('calculator.uploadError'),
      );
    } else if (failed > 0) {
      this.notificationService.warn(
        this.transloco.translate('calculator.documentsPartiallyUploaded', {
          uploaded: total - failed,
          failed,
        }),
      );
    } else {
      this.notificationService.success(
        this.transloco.translate('calculator.documentsUploaded') +
          ' - ' +
          this.transloco.translate('calculator.processingTimeEstimate', {
            minutes: this.batchUploadService.estimatedMinutes(total),
          }),
      );
    }
    this.batchUploadService.reset();
  }

  /**
   * Warn before leaving the tab/window while an upload is still running:
   * closing now aborts the in-flight S3 PUTs and the finalize call, so the
   * batch would never be analyzed. Browsers show their own generic prompt
   * (text can't be customized) and only honor this when an upload is active.
   */
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.batchUploadService.state().status !== 'idle') {
      event.preventDefault();
      // Legacy requirement for Chrome to actually show the confirmation dialog.
      event.returnValue = '';
    }
  }
}
